import {
  coreExtensionData,
  createFrontendModule,
  ExtensionBoundary,
} from '@backstage/frontend-plugin-api';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import { AdminOnlyCatalogIndex } from './AdminOnlyCatalogIndex';

/**
 * The catalog index (`page:catalog`), kept as the catalog plugin declares it
 * — its id, path, route ref, title and nav entry (the original factory's
 * outputs), its config (`path`, `title`, `pagination`) — with its element
 * replaced: the index of mockup « T-a » (CatalogIndexPage.tsx) instead of
 * the PageBlueprint's layout (plugin header bar) around upstream's
 * `NfsDefaultCatalogPage`, which drew its own BUI header.
 *
 * Kept from upstream's page: the list provider and its `pagination` config,
 * every filter of the default `catalog-filter` extensions (re-drawn as
 * pills, on the same filter classes), the table's columns per kind and its
 * view / edit / star actions, the « Créer » button's target (the catalog's
 * `createComponent` external route, the scaffolder by default). Not kept:
 * the elements attached to the `filters` input (the default ones are
 * re-drawn; none other is installed), the export button (`exportSettings`,
 * disabled unless configured — it is not) and the support button.
 *
 * The element is wrapped in ExtensionBoundary.lazy, as the blueprint wraps
 * its loader: the page's code loads with the page, and an error in it stays
 * inside the page's boundary. The admin-only wrap goes around all of it, so
 * a non-admin sees no part of the page and loads none of its code.
 */
const adminOnlyCatalogIndexPage = catalogPlugin
  .getExtension('page:catalog')
  .override({
    factory(originalFactory, { node, config }) {
      const original = originalFactory();
      const pagination = config.pagination;
      // The configured title (app-config: « Catalogue »), as the blueprint
      // titles its header bar; « Catalogue » when none is configured, rather
      // than the plugin's English « Catalog ».
      const title = config.title ?? 'Catalogue';
      return [
        ...Array.from(original).filter(
          value => value.id !== coreExtensionData.reactElement.id,
        ),
        coreExtensionData.reactElement(
          <AdminOnlyCatalogIndex>
            {ExtensionBoundary.lazy(node, async () => {
              const { CatalogIndexPage } = await import('./CatalogIndexPage');
              return <CatalogIndexPage title={title} pagination={pagination} />;
            })}
          </AdminOnlyCatalogIndex>,
        ),
      ];
    },
  });

/**
 * On the `catalog` plugin: an extension of a module replaces the plugin's
 * extension with the same id. Registered in packages/app/src/App.tsx.
 */
export const catalogIndexModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [adminOnlyCatalogIndexPage],
});
